import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Plus, Info, Layers, HelpCircle } from 'lucide-react';
import { useTour } from '../hooks/useTour';
import GuidedTour from '../components/GuidedTour';
import { useToast } from '../contexts/ToastContext';
import axios from 'axios';
import { isAuthenticated, getAuthHeader } from '../services/otpAuthService';

interface Pratica {
  categoria: string;
  catalogo: string;
  descrizionePratica: string;
  codicePratica: string;
  tariffe?: {
    diritti?: number;
    bolli1?: number;
    bolli2?: number;
    costo?: number;
    diritti2?: number;
    diritti3?: number;
    diritti4?: number;
    gas?: number;
    contributi?: number;
    dirittiSted?: number;
    corrispettivoSted?: number;
    tributiSpeciali?: number;
    rimborsoMissioni?: number;
    esenzione?: boolean;
    cumulabile?: boolean;
  };
}

const RimborsoVoucherNuovoPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [tariffario, setTariffario] = useState('NAZIONALE');
  const [searchQuery, setSearchQuery] = useState('');
  const [praticheSuggerite, setPraticheSuggerite] = useState<Pratica[]>([]);
  const [praticaSelezionata, setPraticaSelezionata] = useState<Pratica | null>(null);
  // const [causale, setCausale] = useState(''); // Commentato per uso futuro
  const [quantita, setQuantita] = useState(1);
  const [isCumulativo, setIsCumulativo] = useState(false);
  const [numeroPratiche, setNumeroPratiche] = useState(2);
  // const [note, setNote] = useState(''); // Commentato per uso futuro

  // Guided tour
  const { runTour, tourKey, startTour, handleCallback } = useTour('voucher_nuovo_tour');

  const tourSteps = [
    {
      target: '#tour-nuovo-tariffario',
      content: 'Seleziona il tariffario di riferimento per la pratica.',
      skipBeacon: true,
      placement: 'bottom' as const
    },
    {
      target: '#tour-nuovo-ricerca',
      content: 'Cerca la pratica digitando il codice o la descrizione, minimo 2 caratteri.',
      placement: 'bottom' as const
    },
    {
      target: '#tour-nuovo-quantita',
      content: 'Indica quanti voucher vuoi rendere disponibili.',
      placement: 'top' as const
    },
    {
      target: '#tour-nuovo-submit',
      content: 'Quando sei pronto, clicca qui per pubblicare il voucher.',
      placement: 'top' as const
    }
  ];

  const API_BASE_URL = process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace('/graphql', '')
    : 'http://localhost:5000';

  useEffect(() => {
    if (!isAuthenticated()) {
      toast.error('Devi effettuare l\'accesso');
      navigate('/accesso-associati');
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [navigate]);

  // Cerca pratiche quando l'utente digita
  useEffect(() => {
    // Non cercare se c'è già una pratica selezionata
    if (praticaSelezionata) {
      return;
    }

    const delaySearch = setTimeout(() => {
      if (searchQuery.length >= 2) {
        cercaPratiche();
      } else {
        setPraticheSuggerite([]);
      }
    }, 300);

    return () => clearTimeout(delaySearch);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [searchQuery, tariffario, praticaSelezionata]);

  const cercaPratiche = async () => {
    try {
      setSearchLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/voucher-pratiche/search?tariffario=${tariffario}&q=${searchQuery}&limit=10`,
        { headers: getAuthHeader() }
      );
      setPraticheSuggerite(response.data.data);
    } catch (error: any) {
      console.error('Errore ricerca pratiche:', error);
      toast.error('Errore durante la ricerca delle pratiche');
    } finally {
      setSearchLoading(false);
    }
  };

  const selezionaPratica = (pratica: Pratica) => {
    setPraticaSelezionata(pratica);
    setSearchQuery(pratica.descrizionePratica);
    setPraticheSuggerite([]);
    // Reset cumulativo quando si cambia pratica
    setIsCumulativo(false);
    setNumeroPratiche(2);
  };

  // Verifica se la pratica selezionata è cumulabile
  const isCumulabile = praticaSelezionata?.tariffe?.cumulabile === true;

  // Calcolo bolli cumulativi (stessa logica del backend)
  const calcolaBolliCumulativi = (bolloSingola: number, n: number): number => {
    if (!bolloSingola || bolloSingola <= 0) return 0;
    const bolloRidotto = Math.floor(bolloSingola / 32) * 16;
    return bolloSingola + ((n - 1) * bolloRidotto);
  };

  // Preview tariffe cumulative
  const tariffeCumulativePreview = useMemo(() => {
    if (!isCumulativo || !praticaSelezionata?.tariffe || numeroPratiche < 2) return null;

    const t = praticaSelezionata.tariffe;
    const n = numeroPratiche;
    const formatNum = (v: number) => parseFloat(v.toFixed(2));

    const diritti = t.diritti ? formatNum(t.diritti * n) : 0;
    const diritti2 = t.diritti2 ? formatNum(t.diritti2 * n) : 0;
    const diritti3 = t.diritti3 ? formatNum(t.diritti3 * n) : 0;
    const diritti4 = t.diritti4 ? formatNum(t.diritti4 * n) : 0;
    const bolli1 = t.bolli1 ? calcolaBolliCumulativi(t.bolli1, n) : 0;
    const bolli2 = t.bolli2 ? calcolaBolliCumulativi(t.bolli2, n) : 0;
    const gas = t.gas ? formatNum(t.gas * n) : 0;
    const costo = t.costo ? formatNum(t.costo * n) : 0;
    const contributi = t.contributi ? formatNum(t.contributi * n) : 0;
    const dirittiSted = t.dirittiSted ? formatNum(t.dirittiSted * n) : 0;
    const corrispettivoSted = t.corrispettivoSted ? formatNum(t.corrispettivoSted * n) : 0;
    const tributiSpeciali = t.tributiSpeciali ? formatNum(t.tributiSpeciali * n) : 0;
    const rimborsoMissioni = t.rimborsoMissioni ? formatNum(t.rimborsoMissioni * n) : 0;

    const totale = diritti + diritti2 + diritti3 + diritti4 + bolli1 + bolli2 + gas + costo + contributi + dirittiSted + corrispettivoSted + tributiSpeciali + rimborsoMissioni;

    return {
      diritti: diritti + diritti2 + diritti3 + diritti4,
      bolli: bolli1 + bolli2,
      gas,
      costo,
      contributi,
      dirittiSted,
      corrispettivoSted,
      tributiSpeciali,
      rimborsoMissioni,
      totale
    };
  }, [isCumulativo, praticaSelezionata, numeroPratiche]);

  // Mappa label leggibili per i campi tariffe
  const tariffeLabels: Record<string, string> = {
    diritti: 'Diritti',
    bolli1: 'Bolli',
    bolli2: 'Bolli 2',
    costo: 'Costo Targa',
    gas: 'Gas Compressi',
    contributi: 'Contributi',
    diritti2: 'Diritti Stampato',
    diritti3: 'Diritti Esami',
    diritti4: 'Diritti Idoneità',
    dirittiSted: 'Diritti STED',
    corrispettivoSted: 'Corrispettivo STED',
    tributiSpeciali: 'Tributi Speciali',
    rimborsoMissioni: 'Rimborso Missioni',
  };

  // Funzione per formattare tutti gli importi della tariffa
  const getImportiPrincipali = (tariffe?: Pratica['tariffe']) => {
    if (!tariffe) return null;

    const importi: string[] = [];

    const formatImporto = (valore: any) => {
      const num = typeof valore === 'string' ? parseFloat(valore) : valore;
      return !isNaN(num) && num > 0 ? num.toFixed(2) : null;
    };

    for (const [key, label] of Object.entries(tariffeLabels)) {
      const val = formatImporto((tariffe as any)[key]);
      if (val) importi.push(`${label}: €${val}`);
    }

    return importi.length > 0 ? importi.join(' • ') : null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!praticaSelezionata) {
      toast.error('Seleziona una pratica dalla lista');
      return;
    }

    if (isCumulativo && numeroPratiche < 2) {
      toast.error('Il numero di pratiche cumulative deve essere almeno 2');
      return;
    }

    if (!isCumulativo && quantita < 1) {
      toast.error('La quantità deve essere almeno 1');
      return;
    }

    try {
      setLoading(true);

      const payload: any = {
        tariffario,
        pratica: {
          categoria: praticaSelezionata.categoria,
          catalogo: praticaSelezionata.catalogo,
          descrizionePratica: praticaSelezionata.descrizionePratica,
          codicePratica: praticaSelezionata.codicePratica
        },
      };

      if (isCumulativo) {
        payload.isCumulativo = true;
        payload.numeroPratiche = numeroPratiche;
        payload.quantita = 1; // Cumulativo = sempre 1 voucher
      } else {
        payload.quantita = quantita;
      }

      await axios.post(
        `${API_BASE_URL}/api/voucher-rimborso`,
        payload,
        { headers: getAuthHeader() }
      );

      toast.success(isCumulativo
        ? `Voucher cumulativo (${numeroPratiche} pratiche) caricato con successo!`
        : 'Voucher caricato con successo!'
      );
      navigate('/area-riservata/rimborso-voucher');

    } catch (error: any) {
      console.error('Errore creazione voucher:', error);
      toast.error(error.response?.data?.message || 'Errore durante la creazione del voucher');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <GuidedTour steps={tourSteps} run={runTour} tourKey={tourKey} onCallback={handleCallback} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/area-riservata/rimborso-voucher')}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Torna ai Voucher
          </button>

          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Carica Nuovo Voucher
            </h1>
            <button
              onClick={startTour}
              className="p-2 rounded-lg text-gray-400 hover:text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-colors"
              title="Guida"
            >
              <HelpCircle className="w-5 h-5" />
            </button>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Metti a disposizione un voucher PagoPA non utilizzato
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 space-y-6">
          
          {/* Tariffario */}
          <div id="tour-nuovo-tariffario">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tariffario *
            </label>
            <select
              value={tariffario}
              onChange={(e) => {
                setTariffario(e.target.value);
                setPraticaSelezionata(null);
                setSearchQuery('');
              }}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              required
            >
              <option value="NAZIONALE">Nazionale</option>
              <option value="TRENTO">Provincia Autonoma di Trento</option>
              <option value="BOLZANO">Provincia Autonoma di Bolzano</option>
              <option value="SICILIA">Regione Sicilia</option>
              <option value="AOSTA">Valle d'Aosta</option>
              <option value="FRIULI_VENEZIA_GIULIA">Friuli Venezia Giulia</option>
            </select>
          </div>

          {/* Ricerca Pratica */}
          <div id="tour-nuovo-ricerca">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Pratica *
            </label>
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    // Se l'utente modifica il campo, resetta la selezione
                    if (praticaSelezionata) {
                      setPraticaSelezionata(null);
                    }
                  }}
                  placeholder="Cerca per descrizione o codice tariffa..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  required
                />
                {searchLoading && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                  </div>
                )}
              </div>

              {/* Suggerimenti */}
              {praticheSuggerite.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {praticheSuggerite.map((pratica, index) => {
                    const importi = getImportiPrincipali(pratica.tariffe);
                    return (
                      <div
                        key={index}
                        onMouseDown={(e) => {
                          e.preventDefault(); // Previene il blur dell'input
                        }}
                        onClick={() => selezionaPratica(pratica)}
                        className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-600 border-b border-gray-200 dark:border-gray-600 last:border-b-0 cursor-pointer"
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded">
                              {pratica.codicePratica}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {pratica.categoria}
                            </span>
                          </div>
                          {importi && (
                            <span className="text-xs text-green-600 dark:text-green-400 font-medium whitespace-nowrap">
                              {importi}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {pratica.descrizionePratica}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {praticaSelezionata && (
              <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-purple-600 dark:text-purple-400">
                      {praticaSelezionata.codicePratica}
                    </span>
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      {praticaSelezionata.categoria}
                    </span>
                  </div>
                  {getImportiPrincipali(praticaSelezionata.tariffe) && (
                    <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                      {getImportiPrincipali(praticaSelezionata.tariffe)}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-900 dark:text-white">
                  {praticaSelezionata.descrizionePratica}
                </p>
              </div>
            )}
          </div>

          {/* Toggle Cumulativo - solo se pratica cumulabile */}
          {praticaSelezionata && isCumulabile && (
            <div className="border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Layers className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Pratica Cumulativa</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Pagamento unico per più pratiche identiche con bolli scalati
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsCumulativo(!isCumulativo)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isCumulativo ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isCumulativo ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>
          )}

          {/* Numero Pratiche (se cumulativo) o Quantità (se normale) */}
          {isCumulativo ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Numero Pratiche Cumulative *
              </label>
              <input
                type="number"
                value={numeroPratiche}
                onChange={(e) => setNumeroPratiche(Math.max(2, parseInt(e.target.value) || 2))}
                min="2"
                className="w-full px-4 py-3 border border-amber-300 dark:border-amber-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                required
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Verrà creato 1 voucher cumulativo per {numeroPratiche} pratiche
              </p>
            </div>
          ) : (
            <div id="tour-nuovo-quantita">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Quantità *
              </label>
              <input
                type="number"
                value={quantita}
                onChange={(e) => setQuantita(parseInt(e.target.value) || 1)}
                min="1"
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                required
              />
            </div>
          )}

          {/* Anteprima Calcolo Cumulativo */}
          {isCumulativo && tariffeCumulativePreview && (
            <div className="border border-amber-200 dark:border-amber-700 bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
              <div className="bg-amber-100 dark:bg-amber-900/30 px-4 py-2 border-b border-amber-200 dark:border-amber-700">
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Anteprima Calcolo per {numeroPratiche} pratiche cumulative
                </p>
              </div>
              <div className="p-4 space-y-2">
                {tariffeCumulativePreview.diritti > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Diritti ({numeroPratiche} × €{(praticaSelezionata?.tariffe?.diritti || 0).toFixed(2)})</span>
                    <span className="font-medium text-gray-900 dark:text-white">€{tariffeCumulativePreview.diritti.toFixed(2)}</span>
                  </div>
                )}
                {tariffeCumulativePreview.bolli > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      Bolli (cumulativi)
                    </span>
                    <span className="font-medium text-green-600 dark:text-green-400">€{tariffeCumulativePreview.bolli.toFixed(2)}</span>
                  </div>
                )}
                {tariffeCumulativePreview.gas > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Gas compressi ({numeroPratiche} × €{(praticaSelezionata?.tariffe?.gas || 0).toFixed(2)})</span>
                    <span className="font-medium text-gray-900 dark:text-white">€{tariffeCumulativePreview.gas.toFixed(2)}</span>
                  </div>
                )}
                {tariffeCumulativePreview.costo > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Costo targa ({numeroPratiche} × €{(praticaSelezionata?.tariffe?.costo || 0).toFixed(2)})</span>
                    <span className="font-medium text-gray-900 dark:text-white">€{tariffeCumulativePreview.costo.toFixed(2)}</span>
                  </div>
                )}
                {tariffeCumulativePreview.contributi > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Contributi ({numeroPratiche} × €{(praticaSelezionata?.tariffe?.contributi || 0).toFixed(2)})</span>
                    <span className="font-medium text-gray-900 dark:text-white">€{tariffeCumulativePreview.contributi.toFixed(2)}</span>
                  </div>
                )}
                {tariffeCumulativePreview.dirittiSted > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Diritti STED ({numeroPratiche} × €{(praticaSelezionata?.tariffe?.dirittiSted || 0).toFixed(2)})</span>
                    <span className="font-medium text-gray-900 dark:text-white">€{tariffeCumulativePreview.dirittiSted.toFixed(2)}</span>
                  </div>
                )}
                {tariffeCumulativePreview.corrispettivoSted > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Corrispettivo STED ({numeroPratiche} × €{(praticaSelezionata?.tariffe?.corrispettivoSted || 0).toFixed(2)})</span>
                    <span className="font-medium text-gray-900 dark:text-white">€{tariffeCumulativePreview.corrispettivoSted.toFixed(2)}</span>
                  </div>
                )}
                {tariffeCumulativePreview.tributiSpeciali > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Tributi Speciali ({numeroPratiche} × €{(praticaSelezionata?.tariffe?.tributiSpeciali || 0).toFixed(2)})</span>
                    <span className="font-medium text-gray-900 dark:text-white">€{tariffeCumulativePreview.tributiSpeciali.toFixed(2)}</span>
                  </div>
                )}
                {tariffeCumulativePreview.rimborsoMissioni > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Rimborso Missioni ({numeroPratiche} × €{(praticaSelezionata?.tariffe?.rimborsoMissioni || 0).toFixed(2)})</span>
                    <span className="font-medium text-gray-900 dark:text-white">€{tariffeCumulativePreview.rimborsoMissioni.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                  <div className="flex justify-between text-sm font-bold">
                    <span className="text-gray-900 dark:text-white">Totale Cumulativo</span>
                    <span className="text-amber-600 dark:text-amber-400">€{tariffeCumulativePreview.totale.toFixed(2)}</span>
                  </div>
                </div>

                {/* Confronto risparmio */}
                {praticaSelezionata?.tariffe?.bolli1 && (
                  <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="text-xs text-green-700 dark:text-green-400 font-medium">
                      💰 Risparmio bolli: €{(
                        ((praticaSelezionata.tariffe.bolli1 || 0) * numeroPratiche) - tariffeCumulativePreview.bolli
                      ).toFixed(2)} rispetto a {numeroPratiche} pratiche normali
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate('/area-riservata/rimborso-voucher')}
              className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-semibold transition-colors"
            >
              Annulla
            </button>
            <button
              id="tour-nuovo-submit"
              type="submit"
              disabled={loading || !praticaSelezionata}
              className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Caricamento...
                </>
              ) : (
                <>
                  {isCumulativo ? <Layers className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                  {isCumulativo ? `Carica Voucher Cumulativo (${numeroPratiche} pratiche)` : 'Carica Voucher'}
                </>
              )}
            </button>
          </div>
        </form>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
            💡 Suggerimento
          </h3>
          <p className="text-sm text-blue-800 dark:text-blue-200">
            Dopo aver caricato il voucher, altri associati potranno prenotarlo. 
            Riceverai una notifica via email e potrai comunicare tramite la messaggistica interna 
            per concordare i dettagli dello scambio.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RimborsoVoucherNuovoPage;
